// Pure helpers for MULTI_CHOICE SOP step config, validation, and report resolution.

export type MultipleChoiceOption = {
  id: string;
  label: string;
  report_output: string;
  next_step_id: number | null;
};

export type MultipleChoiceConfig = {
  options: MultipleChoiceOption[];
};

export type MultipleChoiceReportContext = {
  prompt?: string;
  title?: string;
};

export type MultipleChoiceValidationSeverity = 'block' | 'warn';

export type MultipleChoiceValidationIssue = {
  severity: MultipleChoiceValidationSeverity;
  code: string;
  message: string;
  optionId?: string;
};

export const MULTIPLE_CHOICE_REPORT_PLACEHOLDER_TOKENS = ['{prompt}', '{title}'] as const;

function normalizeStepIdValue(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric;
}

function slugifyLabel(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
  return slug || 'option';
}

function deriveStableOptionId(
  raw: Record<string, unknown>,
  index: number,
  usedIds: Set<string>,
): string {
  const explicit = String(raw.id ?? '').trim();
  if (explicit) return explicit;
  const label = String(raw.label ?? raw.value ?? '').trim();
  let base = label ? `option_${slugifyLabel(label)}` : `option_${index + 1}`;
  let candidate = base;
  let suffix = 2;
  while (usedIds.has(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

function normalizeRawOption(
  raw: unknown,
  index: number,
  usedIds: Set<string>,
): MultipleChoiceOption | null {
  if (raw == null || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const label = String(obj.label ?? obj.value ?? '');
  const id = deriveStableOptionId(obj, index, usedIds);
  usedIds.add(id);
  const reportRaw = obj.report_output ?? obj.reportText ?? obj.report_output_text ?? obj.reportOutput;
  return {
    id,
    label,
    report_output: reportRaw == null ? '' : String(reportRaw),
    next_step_id: normalizeStepIdValue(obj.next_step_id ?? obj.nextStepId),
  };
}

function assignStableOptionIds(options: MultipleChoiceOption[]): MultipleChoiceOption[] {
  const usedIds = new Set<string>();
  return options.map((option, index) => {
    let id = String(option.id || '').trim();
    if (!id || usedIds.has(id)) {
      id = deriveStableOptionId({ id: option.id, label: option.label }, index, usedIds);
    }
    usedIds.add(id);
    return { ...option, id };
  });
}

/** Preserve in-progress whitespace; only stabilizes option ids for React keys and routing. */
export function assignMultipleChoiceOptionIdsForEdit(raw: unknown): MultipleChoiceConfig {
  return { options: assignStableOptionIds(parseMultipleChoiceConfig(raw).options) };
}

export function parseMultipleChoiceConfig(raw: unknown): MultipleChoiceConfig {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return { options: [] };
    }
  }
  if (source == null || typeof source !== 'object') {
    return { options: [] };
  }
  const obj = source as Record<string, unknown>;
  const rawOptions = Array.isArray(obj.options) ? obj.options : [];
  const usedIds = new Set<string>();
  const options = rawOptions
    .map((entry, index) => normalizeRawOption(entry, index, usedIds))
    .filter((entry): entry is MultipleChoiceOption => entry != null);
  return { options };
}

export function normalizeMultipleChoiceConfigForSave(raw: unknown): MultipleChoiceConfig {
  return {
    options: assignStableOptionIds(parseMultipleChoiceConfig(raw).options).map((option) => ({
      id: option.id,
      label: String(option.label || '').trim(),
      report_output: String(option.report_output || '').trim(),
      next_step_id: normalizeStepIdValue(option.next_step_id),
    })),
  };
}

export function isMultipleChoiceStep(archetype: unknown): boolean {
  return String(archetype || '').toUpperCase() === 'MULTI_CHOICE';
}

export function getMultipleChoiceDestinations(config: unknown): Array<{ optionId: string; label: string; nextStepId: number | null }> {
  return parseMultipleChoiceConfig(config).options.map((option) => ({
    optionId: option.id,
    label: option.label,
    nextStepId: option.next_step_id,
  }));
}

function substituteMultipleChoicePlaceholders(text: string, context: MultipleChoiceReportContext): string {
  const prompt = String(context.prompt || '').replace(/\?+$/, '').trim();
  const title = String(context.title || '').trim();
  return text
    .replace(/{prompt}/g, prompt)
    .replace(/{title}/g, title);
}

function findMultipleChoiceOption(
  options: MultipleChoiceOption[],
  selectedOptionId: string,
  selectedOptionLabel: string,
): MultipleChoiceOption | undefined {
  const id = String(selectedOptionId || '').trim();
  if (id) {
    const byId = options.find((option) => option.id === id);
    if (byId) return byId;
  }
  const label = String(selectedOptionLabel || '').trim();
  if (!label) return undefined;
  const exact = options.find((option) => option.label === label);
  if (exact) return exact;
  const lower = label.toLocaleLowerCase();
  return options.find((option) => option.label.toLocaleLowerCase() === lower);
}

export type MultipleChoiceReportResolutionOptions = {
  /** Snapshot captured at selection time — used when SOP config is stale or unavailable. */
  loggedReportOutput?: unknown;
};

function resolveLoggedMultipleChoiceReportOutput(
  loggedReportOutput: unknown,
  context: MultipleChoiceReportContext,
): string {
  const logged = String(loggedReportOutput ?? '').trim();
  if (!logged) return '';
  return substituteMultipleChoicePlaceholders(logged, context);
}

/**
 * Resolve the customer report line for a MULTI_CHOICE selection.
 * Empty means omit: never fall back to option label or "Selected option".
 * Logged snapshots that are only the option label are ignored when config report_output is empty
 * (legacy overlay clients used to snapshot label as reportOutput).
 */
export function resolveMultipleChoiceReportLine(
  selectedOptionId: unknown,
  selectedOptionLabel: unknown,
  config: unknown,
  context: MultipleChoiceReportContext,
  options?: MultipleChoiceReportResolutionOptions,
): string {
  const optionsList = parseMultipleChoiceConfig(config).options;
  const option = findMultipleChoiceOption(
    optionsList,
    String(selectedOptionId || ''),
    String(selectedOptionLabel || ''),
  );
  const configuredReportOutput = option ? String(option.report_output || '').trim() : '';
  if (option && configuredReportOutput) {
    return substituteMultipleChoicePlaceholders(configuredReportOutput, context);
  }

  const loggedLine = resolveLoggedMultipleChoiceReportOutput(
    options?.loggedReportOutput,
    context,
  );
  if (loggedLine) {
    const label = String(selectedOptionLabel || option?.label || '').trim();
    // Ignore label-identical snapshots when config text is empty (old overlay clients).
    if (!configuredReportOutput && label && loggedLine === label) {
      return '';
    }
    return loggedLine;
  }

  return '';
}

export function multipleChoiceOptionsMissingReportText(config: unknown): MultipleChoiceOption[] {
  return parseMultipleChoiceConfig(config).options.filter(
    (option) => option.label.trim() && !String(option.report_output || '').trim(),
  );
}

export function multipleChoiceValidationIssues(
  config: unknown,
  options?: { includeInReport?: boolean },
): MultipleChoiceValidationIssue[] {
  const parsed = parseMultipleChoiceConfig(config);
  const issues: MultipleChoiceValidationIssue[] = [];
  const includeInReport = options?.includeInReport !== false;

  if (parsed.options.length < 2) {
    issues.push({
      severity: 'block',
      code: 'min_options',
      message: 'Add at least two choices.',
    });
  }

  for (let index = 0; index < parsed.options.length; index += 1) {
    const option = parsed.options[index];
    if (!String(option.label || '').trim()) {
      issues.push({
        severity: 'block',
        code: 'empty_label',
        message: `Choice ${index + 1} needs an operator label.`,
        optionId: option.id,
      });
    }
    if (includeInReport && option.label.trim() && !String(option.report_output || '').trim()) {
      issues.push({
        severity: 'warn',
        code: 'empty_report_output',
        message: `Choice ${index + 1} ("${option.label.trim()}") is missing report output text.`,
        optionId: option.id,
      });
    }
  }

  return issues;
}

export function multipleChoiceConfigBlocksSave(
  config: unknown,
  options?: { includeInReport?: boolean },
): boolean {
  return multipleChoiceValidationIssues(config, options).some((issue) => issue.severity === 'block');
}

function remapCloneDestination(
  sourceId: number | null,
  idMap: Map<number, number>,
): number | null {
  if (sourceId == null) return null;
  const mapped = idMap.get(sourceId);
  return mapped != null ? mapped : null;
}

/** Remap MULTI_CHOICE option destinations when cloning steps within one SOP. */
export function remapMultipleChoiceConfigDestinationsForClone(
  config: unknown,
  idMap: Map<number, number>,
): MultipleChoiceConfig {
  const parsed = parseMultipleChoiceConfig(config);
  return {
    options: parsed.options.map((option) => ({
      id: option.id,
      label: option.label,
      report_output: option.report_output,
      next_step_id: remapCloneDestination(option.next_step_id, idMap),
    })),
  };
}

/** Clear MULTI_CHOICE option destinations that point at a deleted step; preserve other metadata. */
export function unlinkMultipleChoiceDestinationFromConfig(
  config: unknown,
  deletedStepId: number,
): MultipleChoiceConfig | null {
  const parsed = parseMultipleChoiceConfig(config);
  if (!parsed.options.length) return null;

  let changed = false;
  const options = parsed.options.map((option) => {
    if (option.next_step_id !== deletedStepId) return option;
    changed = true;
    return { ...option, next_step_id: null };
  });

  return changed ? { options } : null;
}
